<svelte:options
	customElement={{
		tag: 'rf-canvas-annotation',
		shadow: 'open', // shadow: 'open'
	}}
/>

<script module>
	/**
	 * Types
	 */

	export interface ReframeCanvasAnnotationProps {
		x: number;
		y: number;
		width: number;
		height: number;
		className?: string;
		src?: string;
		href?: string;
		label?: string;
		fit?: 'contain' | 'cover' | 'auto';
		background?: string;
		border?: string;
	}
</script>

<script lang="ts">
	/**
	 * Imports
	 */
	import type { AnnotationDisplay } from '@digirati/canvas-panel-web-components';
	import type { CanvasPanelWebComponent } from '@src/types/canvas-panel';
	import { waitUntilAvailable } from '@src/utils/waitUntilAvailable';
	import { createEventDispatcher } from 'svelte';
	import { v4 as uuidv4 } from 'uuid';

	/**
	 * Props
	 */
	let {
		x = 0,
		y = 0,
		width = 100,
		height = 100,
		className = '',
		src = '',
		fit = 'auto',
		href = '',
		background = 'rgba(255, 255, 255, 0.5)',
		border = '1px solid rgba(0, 0, 0, 0.5)',
	}: ReframeCanvasAnnotationProps = $props();

	/**
	 * State
	 */
	let annotation: AnnotationDisplay | null = $state(null);
	let canvasPanel: CanvasPanelWebComponent | null = $state(null);

	/**
	 * Events
	 */
	const dispatch = createEventDispatcher();

	/**
	 * Methods
	 */

	export const setCanvasPanel = (panel: CanvasPanelWebComponent) => {
		canvasPanel = panel;
	};

	export const getAnnotation = async (canvasId: string) => {
		const annotationShell = {
			id: uuidv4(),
			type: 'Annotation',
			motivation: [href ? 'linking' : 'highlighting'],
			body: {
				type: 'TextualBody',
				value: 'Example Highlight',
				format: 'text/plain',
			},
			target: {
				id: canvasId + `#xywh=${x},${y},${width},${height}`,
				type: 'Canvas',
			},
		};

		annotation = await createAnnotationDisplay(annotationShell);

		if (!annotation) return;

		if (src) {
			annotation.applyStyle({
				background: `url("${src}") no-repeat center center`,
				// @ts-expect-error
				backgroundSize: fit,
			});
		}
		if (href) {
			annotation.href = href;

			annotation.applyStyle({
				background: background,
				border: border,
			});
		}

		annotation.className = className;

		return annotation;
	};

	export const createAnnotationDisplay = async (annotation: any): Promise<AnnotationDisplay | null> => {
		const panel = await waitUntilAvailable(() => canvasPanel);
		// else check wait for canvas panel
		if (!panel) return null;

		const linkingAnnotation = await panel.vault.load(annotation.id, annotation);

		const displayAnnotation = panel.createAnnotationDisplay(linkingAnnotation) as unknown;

		// @ts-expect-error
		displayAnnotation.addEventListener('onClick', (target, anno) => {
			dispatch('click', {
				target,
				annotation: anno,
			});
		});

		panel.annotations.add(displayAnnotation);

		return displayAnnotation as AnnotationDisplay;
	};

	export {};
</script>

<div class="rf-canvas-annotation">
	<slot />
</div>

<style lang="scss" global>
	.rf-canvas-annotation {
		position: absolute;
		visibility: hidden;
	}
</style>
